import Papa from 'papaparse';
import type { TradeItem } from '@/types';
import { isOpenBookStatus } from '@/types';
import { buildCsvTotalsRows, SYNTHETIC_CSV_ACCESSORS } from './csv-totals';
import { csvFilenameStamp } from './csv-filename';
import { recordExportEvent } from '@/services/audit/audit-service';

type ColumnConfig = {
  id: string;
  label: string;
  visible: boolean;
};

const generateFilename = (): string => `trades-${csvFilenameStamp()}.csv`;

export const exportTradesToCSV = (
  data: TradeItem[],
  visibleColumns: ColumnConfig[],
  baseCurrency = 'EUR',
): void => {
  if (data.length === 0) {
    alert('No data available to export. Please adjust your filters or wait for data to load.');
    return;
  }

  const filename = generateFilename();

  const visibleColumnIds = visibleColumns
    .filter((col) => col.visible && col.id !== 'rowNumber')
    .map((col) => col.id);

  const columnLabels = visibleColumns
    .filter((col) => visibleColumnIds.includes(col.id))
    .reduce((acc, col) => {
      acc[col.id] = col.label;
      return acc;
    }, {} as Record<string, string>);

  const csvData = data.map((row) => {
    const csvRow: Record<string, unknown> = {};
    visibleColumnIds.forEach((colId) => {
      // Display/synthetic columns (e.g. total_value) are NOT plain TradeItem keys —
      // reading row[colId] yields undefined and the cell exported blank. Compute
      // them via the synthetic accessor so the value (and its column total) is real.
      const value =
        colId in SYNTHETIC_CSV_ACCESSORS
          ? SYNTHETIC_CSV_ACCESSORS[colId](row)
          : row[colId as keyof TradeItem];
      csvRow[columnLabels[colId]] = value ?? '';
    });
    return csvRow;
  });

  // Append per-currency subtotals + a EUR/USD-equivalent grand total (LR-469).
  // LR-474 r5 #1: total only the economically-OPEN book — `pending`/`rejected` rows are
  // still EXPORTED as data rows above (operator follow-up), but they carry no real value
  // so they must not inflate the totals block. This keeps the CSV totals in lock-step
  // with the on-page footer (TradesTable footerRows) and the MetricsBar money doctrine.
  const totalsRows = data.filter((r) => isOpenBookStatus(r.status));
  csvData.push(...buildCsvTotalsRows(totalsRows, visibleColumnIds, columnLabels, baseCurrency));

  // escapeFormulae: neutralise spreadsheet formula-injection via free-text cells
  // (symbol/description/etc. come from backend feeds) — parity with mtm-csv-export.
  const csv = Papa.unparse(csvData, { escapeFormulae: true });

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);

  recordExportEvent('trades.export_csv', { row_count: data.length });
};
